/** Pure helpers for the Compare view: differences against the baseline and their "story". */

/** a − b per month. */
export function difference(a: ArrayLike<number>, b: ArrayLike<number>): Float64Array {
  const n = Math.min(a.length, b.length);
  const out = new Float64Array(n);
  for (let i = 0; i < n; i++) out[i] = (a[i] ?? 0) - (b[i] ?? 0);
  return out;
}

/** Differences within half a unit (e.g. half a euro) count as level. */
const LEVEL = 0.5;

const signOf = (v: number): -1 | 0 | 1 => (v > LEVEL ? 1 : v < -LEVEL ? -1 : 0);

export interface DiffStory {
  /** Difference in the last month. */
  final: number;
  /** Where it ends: ahead of, behind or level with the baseline. */
  outcome: 'ahead' | 'behind' | 'level';
  /**
   * The month from which the final outcome holds for good, if the variant was on the other side
   * of the baseline before (an overtake or a fall-behind); null when it holds throughout.
   */
  crossover: number | null;
}

/** How a variant's difference from the baseline develops (e.g. "Buy overtakes Rent in 2033"). */
export function diffStory(diff: ArrayLike<number>): DiffStory {
  const last = diff.length - 1;
  const final = diff[last] ?? 0;
  const end = signOf(final);
  if (end === 0) return { final, outcome: 'level', crossover: null };
  // The last month on the other side, then the first month on the final side after it.
  let opposite = -1;
  for (let i = last; i >= 0 && opposite < 0; i--) if (signOf(diff[i] ?? 0) === -end) opposite = i;
  let crossover: number | null = null;
  if (opposite >= 0) {
    crossover = opposite + 1;
    while (signOf(diff[crossover] ?? 0) !== end) crossover++;
  }
  return { final, outcome: end > 0 ? 'ahead' : 'behind', crossover };
}
